<template>
  <div class="topic-page">
    <b-loading :is-full-page="false" :active.sync="loading"></b-loading>

    <div class="topic-columns">
      <!-- Topic Scenarios Column -->
      <div
        class="scenario-column drop-zone"
        :class="{ 'is-dragover': isDragOver }"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop($event)"
      >
        <div class="panel-heading">
          <div class="panel-heading-left">
            <span class="title is-6 mb-0">
              {{ selectedTopic ? selectedTopic.title : "Topic Examples" }}
            </span>
            <b-tag type="is-primary is-light" class="ml-2">{{
              topicScenarios.length
            }}</b-tag>
          </div>
        </div>
        <ScenarioList
          class="list-container"
          :scenarios="topicScenarios"
          :selectedScenarioId="selectedScenarioId"
          :canEdit="canEdit"
          :enableDragDrop="false"
          @select="handleSelectScenario"
          @create="handleCreateScenario"
        />
      </div>

      <!-- All Scenarios Column -->
      <div class="scenario-column">
        <div class="panel-heading">
          <div class="panel-heading-left">
            <span class="title is-6 mb-0">All Examples</span>
            <b-tag type="is-info is-light" class="ml-2">{{
              totalScenarios
            }}</b-tag>
          </div>
          <div class="panel-heading-right">
            <b-pagination
              v-if="totalPages > 1"
              :total="totalScenarios"
              :current.sync="page"
              :per-page="limit"
              size="is-small"
              :simple="true"
              @change="handlePageChange"
            />
          </div>
        </div>
        <ScenarioList
          class="list-container"
          :scenarios="allScenarios"
          :selectedScenarioId="selectedScenarioId"
          :can-edit="canEdit"
          :enableDragDrop="true"
          @select="handleSelectScenario"
          @create="handleCreateScenario"
        />
      </div>

      <!-- Scenario Preview Column -->
      <div class="preview-column">
        <ScenarioPreview
          v-if="selectedScenario"
          :scenario="selectedScenario"
          :can-edit="canEdit"
          @edit="handleEditScenario"
        />
        <div v-else class="no-selection-message">
          <p>Select an example to view details</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ScenarioList from "@/components/training/explorer/organisms/ScenarioList.vue";
import { TopicsService } from "@/services/CoachingServices";
import { mapGetters } from "vuex";
import ScenarioPreview from "../organisms/ScenarioPreview.vue";

export default {
  name: "TopicPage",

  components: {
    ScenarioList,
    ScenarioPreview,
  },

  data() {
    return {
      allScenarios: [],
      topicScenarios: [],
      selectedTopic: null,
      selectedScenarioId: null,
      selectedScenario: null,
      loading: false,
      page: 1,
      limit: 50,
      totalScenarios: 0,
      isDragOver: false,
    };
  },

  computed: {
    ...mapGetters(["getUserId", "isAdmin", "isTeacher"]),

    canEdit() {
      return (
        this.isAdmin ||
        this.isTeacher ||
        (this.selectedTopic && this.selectedTopic.ownerId === this.getUserId)
      );
    },

    topicId() {
      return this.$route.params.topicId;
    },

    totalPages() {
      return Math.ceil(this.totalScenarios / this.limit);
    },
  },

  watch: {
    topicId: {
      immediate: true,
      handler() {
        this.loadData();
      },
    },
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        // Load the topic
        if (this.topicId) {
          const topics = await TopicsService.getTopicsByIds([this.topicId]);
          if (topics && topics.length > 0) {
            this.selectedTopic = topics[0];
            this.topicScenarios = await TopicsService.getScenarios(
              this.topicId
            );
          }
        }

        // Get total count of scenarios for pagination
        const allScenariosNoLimit = await TopicsService.getAllScenarios({
          page: 1,
          limit: 1000, // Use a large limit to get approximate total count
        });
        this.totalScenarios = allScenariosNoLimit.length;

        // Load all scenarios with pagination
        let allScenarios = await TopicsService.getAllScenarios({
          page: this.page,
          limit: this.limit,
          // Don't filter by topicId to show all scenarios
        });

        // Filter out scenarios that are already in the topic
        if (this.topicScenarios && this.topicScenarios.length > 0) {
          const topicPgns = this.topicScenarios.map((s) => s.pgn);
          allScenarios = allScenarios.filter((s) => !topicPgns.includes(s.pgn));
        }

        this.allScenarios = allScenarios;
      } catch (error) {
        console.error("Error loading data:", error);
        this.$buefy.toast.open({
          message: "Failed to load data",
          type: "is-danger",
        });
      } finally {
        this.loading = false;
      }
    },

    async handlePageChange(page) {
      this.page = page;
      this.loading = true;

      try {
        let allScenarios = await TopicsService.getAllScenarios({
          page: this.page,
          limit: this.limit,
        });

        // Filter out scenarios that are already in the topic
        if (this.topicScenarios && this.topicScenarios.length > 0) {
          const topicPgns = this.topicScenarios.map((s) => s.pgn);
          allScenarios = allScenarios.filter((s) => !topicPgns.includes(s.pgn));
        }

        this.allScenarios = allScenarios;
      } catch (error) {
        console.error("Error loading examples:", error);
        this.$buefy.toast.open({
          message: "Failed to load examples",
          type: "is-danger",
        });
      } finally {
        this.loading = false;
      }
    },

    async handleSelectScenario(scenarioId) {
      this.selectedScenarioId = scenarioId;
      this.selectedScenario =
        this.allScenarios.find((s) => s.id === scenarioId) ||
        this.topicScenarios.find((s) => s.id === scenarioId);
    },

    handleEditTopic() {
      if (this.selectedTopic) {
        // Check if we're in teacher context
        const isTeacherContext = this.$route.meta?.teacher ?? false;
        const topicPath = isTeacherContext
          ? `/teacher/topics/${this.selectedTopic.id}/edit`
          : `/topics/${this.selectedTopic.id}/edit`;
        this.$router.push(topicPath);
      }
    },

    handleCreateScenario() {
      if (this.selectedTopic) {
        // Check if we're in teacher context
        const isTeacherContext = this.$route.meta?.teacher ?? false;
        const scenarioPath = isTeacherContext
          ? `/teacher/topics/${this.selectedTopic.id}/scenarios/new`
          : `/topics/${this.selectedTopic.id}/scenarios/new`;
        this.$router.push(scenarioPath);
      }
    },

    handleEditScenario() {
      if (this.selectedScenario) {
        // Check if we're in teacher context
        const isTeacherContext = this.$route.meta?.teacher ?? false;
        const scenarioPath = isTeacherContext
          ? `/teacher/topics/${this.selectedScenario.topicId}/scenarios/${this.selectedScenario.id}/edit`
          : `/topics/${this.selectedScenario.topicId}/scenarios/${this.selectedScenario.id}/edit`;
        this.$router.push(scenarioPath);
      }
    },

    // Drag and drop handlers
    handleDragOver() {
      if (!this.selectedTopic) return;
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    async handleDrop(event) {
      if (!this.selectedTopic) {
        this.$buefy.toast.open({
          message: "Please select a topic first",
          type: "is-warning",
        });
        return;
      }

      this.isDragOver = false;

      try {
        const scenarioData = JSON.parse(
          event.dataTransfer.getData("text/plain")
        );

        // Find the full scenario data
        const scenario = this.allScenarios.find(
          (s) => s.id === scenarioData.id
        );
        if (!scenario) {
          throw new Error("Scenario not found");
        }

        // Check if scenario with same PGN already exists in topic
        const existingScenario = this.topicScenarios.find(
          (s) => s.pgn === scenario.pgn
        );
        if (existingScenario) {
          this.$buefy.toast.open({
            message: "Example with same PGN already exists in this topic",
            type: "is-info",
          });
          return;
        }

        this.loading = true;

        // Create a copy of the scenario in the current topic
        await TopicsService.createScenario(this.topicId, {
          pgn: scenario.pgn,
          title: scenario.title,
          description: scenario.description,
          isPuzzle: scenario.isPuzzle,
          isHomework: scenario.isHomework,
          puzzleLevel: scenario.puzzleLevel,
          status: scenario.status,
        });

        // Reload topic scenarios
        this.topicScenarios = await TopicsService.getScenarios(this.topicId);

        this.$buefy.toast.open({
          message: "Example added to topic successfully",
          type: "is-success",
        });
      } catch (error) {
        console.error("Error adding example to topic:", error);
        this.$buefy.toast.open({
          message: "Failed to add example to topic",
          type: "is-danger",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.topic-columns {
  gap: 1rem;
  margin: 0;
  height: 90vh;
  display: flex;
}

.topic-columns > div {
  border: 4px solid #dbdbdb;
  padding: 0;
  margin: 0 !important; /* Override Bulma column margins */
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.scenario-column {
  max-width: 400px;
  display: flex;
  flex-direction: column;
}

.list-container {
  flex: 1;
  overflow-y: auto;
}

.preview-column {
  flex: 1;
}

.panel-heading {
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.panel-heading-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.panel-heading-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  min-width: 0; /* Allow container to shrink */
  flex: 1;
}

.drop-zone {
  transition: all 0.3s ease;
}

.drop-zone.is-dragover {
  background-color: rgba(0, 209, 178, 0.1);
  border: 4px dashed #00d1b2;
}

.no-selection-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f5f5;
  color: #7a7a7a;
  font-size: 1.2rem;
  text-align: center;
  padding: 2rem;
}
</style>
